/**
 * ============================================================================
 * METRIC COMMENTS API — RTK Query endpoints for callouts / comments / replies.
 * ============================================================================
 *
 * Cache unit is ONE cell (metricKey × reportingPeriod × dataMode). The drawer
 * subscribes to `getCellComments` for the clicked cell; every write mutation
 * invalidates that cell's tag so the thread refetches automatically — this
 * replaces the legacy CTS UI's manual reload-after-write.
 *
 * Transport + record→UI mapping live in the accessor (MetricCommentsApi);
 * these endpoints are cache policy only (queryFn → accessor).
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { MetricCommentsApi } from 'src/accessors/MetricCommentsApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import {
  CellAnchor,
  CellCalloutSummary,
  CellComments,
} from 'src/types/metricComments.types';
import { DataMode } from 'src/types/metricsApi.types';

/** Args for the week-level scan behind the callouts panel. */
export interface WeekCalloutsArgs {
  /** The served dashboard being viewed (spec multi-dashboard R3.1). */
  dashboardId: string;
  reportingPeriod: string;
  dataMode: DataMode;
}

const cellTag = (anchor: CellAnchor) =>
  // metricCategory is '' until the backend adds the network dimension, so the
  // tag is stable today and fragments per-network the moment cells carry one.
  // NOTE: dataMode fragments the FE CACHE only — it is NOT sent to the backend
  // (the model has no data-mode field), so it implies no server-side isolation.
  // Harmless because callouts are snapshot-only (spec R1.4/R1.5): dataMode is
  // effectively always SNAPSHOT, and it stays here as a forward-compat placeholder.
  `${anchor.dataMode}#${anchor.metricCategory ?? ''}#${anchor.metricKey}#${
    anchor.reportingPeriod
  }`;

/** The week roll-up shares a tag so any write in the period refreshes the panel. */
const weekTag = (dataMode: DataMode, reportingPeriod: string) =>
  `${dataMode}#${reportingPeriod}`;

/** Both the clicked cell and its week are invalidated by a write. */
const writeTags = (anchor: CellAnchor) => [
  { type: 'Cell' as const, id: cellTag(anchor) },
  {
    type: 'Week' as const,
    id: weekTag(anchor.dataMode, anchor.reportingPeriod),
  },
];

function wrapError(error: unknown) {
  if (error instanceof ReportingApiError) {
    return {
      error: { status: error.status, data: error.body ?? error.errorCode },
    };
  }
  return { error: { status: 0, data: String(error) } };
}

/** Run an accessor write, then report success/failure in the RTK shape. */
async function runWrite(op: () => Promise<void>) {
  try {
    await op();
    return { data: undefined };
  } catch (error) {
    return wrapError(error);
  }
}

export const metricCommentsApi = createApi({
  reducerPath: 'metricCommentsApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  tagTypes: ['Cell', 'Week'],
  endpoints: (builder) => ({
    getCellComments: builder.query<CellComments, CellAnchor>({
      queryFn: async (anchor) => {
        try {
          return { data: await MetricCommentsApi.loadCell(anchor) };
        } catch (error) {
          return wrapError(error);
        }
      },
      providesTags: (_result, _error, anchor) => [
        { type: 'Cell', id: cellTag(anchor) },
      ],
    }),

    /** Week-level roll-up behind the callouts panel. */
    getWeekCallouts: builder.query<CellCalloutSummary[], WeekCalloutsArgs>({
      queryFn: async ({ dashboardId, reportingPeriod, dataMode }) => {
        try {
          return {
            data: await MetricCommentsApi.scanWeek(
              dashboardId,
              reportingPeriod,
              dataMode,
            ),
          };
        } catch (error) {
          return wrapError(error);
        }
      },
      providesTags: (_r, _e, { dataMode, reportingPeriod }) => [
        { type: 'Week', id: weekTag(dataMode, reportingPeriod) },
      ],
    }),

    writeCallout: builder.mutation<
      void,
      { anchor: CellAnchor; body: string; existingId: string | null }
    >({
      queryFn: ({ anchor, body, existingId }) =>
        runWrite(() =>
          MetricCommentsApi.writeCallout(anchor, body, existingId),
        ),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),
    deleteCallout: builder.mutation<void, { anchor: CellAnchor; id: string }>({
      queryFn: ({ anchor, id }) =>
        runWrite(() => MetricCommentsApi.deleteCallout(id, anchor)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),

    createComment: builder.mutation<void, { anchor: CellAnchor; body: string }>(
      {
        queryFn: ({ anchor, body }) =>
          runWrite(() => MetricCommentsApi.createComment(anchor, body)),
        invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
      },
    ),
    editComment: builder.mutation<
      void,
      { anchor: CellAnchor; id: string; body: string }
    >({
      queryFn: ({ anchor, id, body }) =>
        runWrite(() => MetricCommentsApi.editComment(id, body, anchor)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),
    deleteComment: builder.mutation<void, { anchor: CellAnchor; id: string }>({
      queryFn: ({ anchor, id }) =>
        runWrite(() => MetricCommentsApi.deleteComment(id, anchor)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),

    createReply: builder.mutation<
      void,
      { anchor: CellAnchor; commentId: string; body: string }
    >({
      queryFn: ({ anchor, commentId, body }) =>
        runWrite(() => MetricCommentsApi.createReply(anchor, commentId, body)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),
    editReply: builder.mutation<
      void,
      { anchor: CellAnchor; id: string; body: string }
    >({
      queryFn: ({ anchor, id, body }) =>
        runWrite(() => MetricCommentsApi.editReply(id, body, anchor)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),
    deleteReply: builder.mutation<void, { anchor: CellAnchor; id: string }>({
      queryFn: ({ anchor, id }) =>
        runWrite(() => MetricCommentsApi.deleteReply(id, anchor)),
      invalidatesTags: (_r, _e, { anchor }) => writeTags(anchor),
    }),
  }),
});

export const {
  useGetCellCommentsQuery,
  useGetWeekCalloutsQuery,
  useWriteCalloutMutation,
  useDeleteCalloutMutation,
  useCreateCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useCreateReplyMutation,
  useEditReplyMutation,
  useDeleteReplyMutation,
} = metricCommentsApi;
